// Dependencies
import { createFileRoute } from "@tanstack/react-router";
import { PageShell, PageHero } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { CtaSection } from "@/components/CtaSection";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";

// Images
import heroVideo from "@/assets/video/mlbr_missao.mp4";
import mlbrMissionProfile from "@/assets/mission.png";
import ctaImage from "@/assets/mlbr.png";

export const Route = createFileRoute("/$lang/mlbr")({
  head: () => ({
    meta: [
      { title: i18n.t("mlbr:title", "Brazilian Microlauncher (MLBR — Brazilian Microlauncher)") + " — BIZU Space" },
      {
        name: "description",
        content: i18n.t("mlbr:metaDesc",
          "BIZU Space's involvement in the Brazilian Microlauncher (MLBR) program, providing liquid propulsion (ARION engine) and systems engineering to achieve sovereign orbit access.",
        ),
      },
      { property: "og:title", content: i18n.t("mlbr:title", "Brazilian Microlauncher (MLBR)") + " — BIZU Space" },
      {
        property: "og:description",
        content: i18n.t("mlbr:metaOgDesc",
          "Developing liquid propulsion (ARION engine) and systems engineering for the Brazilian Microlauncher (MLBR) program.",
        ),
      },
      { property: "og:url", content: "/mlbr" },
    ],
    links: [{ rel: "canonical", href: "https://bizu.space/mlbr" }],
  }),
  component: MlbrPage,
});

const partnerLogos = Object.values(
  import.meta.glob("/src/assets/partners-mlbr/*.{png,jpg,jpeg,svg,webp}", {
    eager: true,
    import: "default",
  }),
) as string[];

function MlbrPage() {
  const { t } = useTranslation("mlbr");

  return (
    <PageShell>
      <PageHero
        title={t("title", "Brazilian Microlauncher - MLBR")}
        description={t("description",
          "Conduct a national satellite into space using a Brazilian rocket, launched from a space center within our territory.",
        )}
        video={heroVideo}
      />

      <section className="py-24">
        <div className="container-bizu">
          <Reveal effect="up">
            <h2 className="text-2xl font-semibold">
              {t("mecbTitle", "Guideline of the Complete Brazilian Space Mission")}
            </h2>

            <p className="mt-4 text-base leading-relaxed">
              {t("mecbText",
                "The Guideline of the Complete Brazilian Space Mission (MECB), established as early as 1978, defined as a matter of national interest the need to equip Brazil with the technologies and means to access space independently.",
              )}
            </p>

            <h2 className="mt-8 text-2xl font-semibold">
              {t("contractsTitle", "MLBR Contracts and Development")}
            </h2>

            <p className="mt-4 text-base leading-relaxed">
              {t("contractsText",
                "At the end of 2023, the country made significant progress toward this goal, driven by FINEP and the AEB (Brazilian Space Agency), through the signing of contracts for the development of national launch vehicles, with execution starting in 2024. The objective is to launch two prototypes, aiming to enable the future insertion of Brazilian companies into the growing global market for small satellite launches—somewhat similar to what occurred more than half a century ago with the development of the Bandeirante aircraft, which laid the foundation for the creation of EMBRAER, addressing both the needs of the Brazilian Air Force and the then-emerging regional air transport market.",
              )}
            </p>

            <h2 className="mt-8 text-2xl font-semibold">
              {t("characteristicsTitle", "Technical Characteristics")}
            </h2>

            <p className="mt-4 text-base leading-relaxed">
              {t("characteristicsText1",
                "The Brazilian Microlauncher (MLBR), currently under development through strong collaboration among eight genuinely Brazilian companies (CENIC, DELSIS, ETSYS, PLASMAHUB, CONCERT, BIZU Space SPACE, ALMEIDA, FIBRAFORTE, HORUSEYE TECH and CLC), will be a three-stage rocket with solid propulsion, approximately 12 meters in height, 1.1 meters in diameter, and a liftoff mass of 12 tons, capable of carrying payloads of up to 40 kg to orbits at 450 km.",
              )}
            </p>

            <p className="mt-4 text-base leading-relaxed">
              {t("characteristicsText2",
                "Its development is of strategic importance for the country, promoting independence, cutting-edge technology, and national capability. Strong public-private partnerships are essential, as observed in space powers such as China, Russia, Japan, India, Europe, and the United States.",
              )}
            </p>

            <h2 className="mt-8 text-2xl font-semibold">
              {t("engineeringTitle", "Multigenerational Engineering")}
            </h2>

            <p className="mt-4 text-base leading-relaxed">
              {t("engineeringText1",
                "The team responsible for MLBR is composed of different generations of engineers, with several graduates from ITA playing a leading technical role. The project brings together senior professionals with extensive experience from the early stages of the Brazilian Space Program and highly motivated young engineers trained in the newer Aerospace Engineering program at ITA. This diversity demonstrates that MLBR was conceived as a long-term project, aiming at technological sustainability and knowledge continuity.",
              )}
            </p>

            <p className="mt-4 text-base leading-relaxed">
              {t("engineeringText2",
                "Finally, it is worth highlighting that the MLBR program was conceived alongside a robust business plan designed to position Brazil as a dynamic supplier in the emerging New Space market, specializing in the launch of nano- and microsatellites.",
              )}
            </p>

            <h2 className="mt-8 text-2xl font-semibold">
              {t("upperStageTitle", "New Upper Stage")}
            </h2>

            <p className="mt-4 text-base leading-relaxed">
              {t("upperStageText1",
                "In this context, a liquid-propellant engine is being developed to replace the upper stage, making it lighter and more efficient. This aims to increase the vehicle’s payload capacity, reduce the cost per kilogram placed into orbit, and improve the precision of satellite positioning.",
              )}
            </p>

            <p className="mt-4 text-base leading-relaxed">
              {t("upperStageText2",
                "For this purpose, a propellant combination of High-Test Peroxide (HTP) and aviation kerosene (Jet-A) was chosen, motivated by lower costs and greater stability compared to engines that use liquid oxygen.",
              )}
            </p>

            <h2 className="mt-8 text-2xl font-semibold">
              {t("challengesTitle", "Challenges and Inspiration")}
            </h2>

            <p className="mt-4 text-base leading-relaxed">
              {t("challengesText1",
                "The challenges are enormous—from material acquisition and testing to bureaucracy and limited access to strategic knowledge—but deeply motivating.",
              )}
            </p>

            <p className="mt-4 text-base leading-relaxed">
              {t("challengesText2",
                "On September 12, 1962, then U.S. President John F. Kennedy delivered words that still resonate with our efforts:",
              )}
            </p>

            <blockquote className="mt-10 border-l-4 border-accent pl-6 text-lg italic leading-relaxed">
              {t("kennedyQuote",
                "“We choose to go to the Moon in this decade and do the other things, not because they are easy, but because they are hard; because that goal will serve to organize and measure the best of our energies and skills, because that challenge is one that we are willing to accept, one we are unwilling to postpone, and one we intend to win, and the others, too”.",
              )}
            </blockquote>

            <p className="mt-4 text-base leading-relaxed">
              {t("armstrongIntro", "On July 20, 1969, less than seven years later, the world heard:")}
            </p>

            <blockquote className="mt-8 border-l-4 border-accent pl-6 text-lg italic leading-relaxed">
              {t("armstrongQuote", "“That’s one small step for a man, one giant leap for mankind”")}
            </blockquote>

            <p className="mt-4 text-base leading-relaxed">
              {t("armstrongOutro", "when Neil Armstrong set foot on the Moon for the first time.")}
            </p>

            <p className="mt-8 text-base leading-relaxed">
              {t("inspiredText",
                "Inspired by this legacy, the MLBR team is fully committed to the mission entrusted to us by the Brazilian people, inheritors of the success we aim to achieve. We believe in the ability of the Triple Helix—academia, industry, and government—to create cutting-edge technology in the country, always relying on the support of academic institutions and government agencies for the success of this ambitious project.",
              )}
            </p>
          </Reveal>

          <Reveal effect="scale" className="mt-14">
            <img
              src={mlbrMissionProfile}
              alt="MLBR Mission Profile"
              className="w-full rounded-2xl object-cover"
            />
          </Reveal>
        </div>
      </section>

      {/* PARTNERS TICKER */}
      <section className="border-y border-border py-24 overflow-hidden">
        <div className="container-bizu overflow-hidden">
          <p className="section-tag justify-center text-center mb-10">
            {t("partners.tag", "Partnerships that accelerate results")}
          </p>

          <div className="relative w-full overflow-hidden">
            <div className="flex w-fit animate-ticker items-center gap-6">
              {[...partnerLogos, ...partnerLogos].map((logo, index) => (
                <div 
                  key={index} 
                  className="flex h-24 w-48 shrink-0 items-center justify-center rounded-2xl bg-white px-8 shadow-sm transition-transform hover:scale-105"
                >
                  <img
                    src={logo}
                    alt="Partner"
                    className="max-h-14 w-auto object-contain mix-blend-multiply"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CtaSection
        image={ctaImage}
        title={t("cta.title", "Want to know more about MLBR?")}
        subtitle={t("cta.subtitle", "Follow our journey and explore the future of Brazilian space technology.")}
        buttonText={t("cta.btn1", "Contact us")}
        href="/contact"
        secondaryButtonText={t("cta.btn2", "Learn more")}
        secondaryButtonHref="https://www.cenic.biz/projeto/lancador-mlbr"
        secondaryButtonHiddenText={t("cta.btn2Aria", "about MLBR at CENIC website")}
      />
    </PageShell>
  );
}
